import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Globe } from 'lucide-react'

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()

  // Check if current path is Chinese
  const isZh = location.pathname.startsWith('/zh')

  // Get the link path with language prefix based on current URL
  const getLocalizedPath = (path: string) => {
    return isZh ? `/zh${path}` : path
  }

  // Get the path without the language prefix
  const getPathWithoutLang = () => {
    const path = location.pathname
    if (path.startsWith('/zh')) {
      return path.slice(3) || '/'
    }
    return path
  }

  // Get the target path for language switch
  const getLanguageSwitchPath = () => {
    const pathWithoutLang = getPathWithoutLang()
    if (i18n.language === 'en') {
      // Switch to Chinese
      return pathWithoutLang === '/' ? '/zh/' : `/zh${pathWithoutLang}`
    } else {
      // Switch to English
      return pathWithoutLang
    }
  }

  const links = [
    { key: 'about', path: '/about' },
    { key: 'privacy', path: '/privacy' },
    { key: 'terms', path: '/terms' },
    { key: 'contact', path: '/contact' }
  ]

  return (
    <motion.footer 
      className="bg-apple-gray-50 border-t border-apple-gray-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Links */}
        <div className="flex flex-wrap justify-center gap-6 mb-6">
          {links.map((link) => (
            <Link key={link.key} to={getLocalizedPath(link.path)}>
              <motion.span
                className="text-sm text-apple-gray-500 hover:text-apple-gray-900 transition-colors duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {t(link.key)}
              </motion.span>
            </Link>
          ))}
        </div>

        {/* Language Toggle */}
        <div className="flex justify-center mb-6">
          <Link to={getLanguageSwitchPath()}>
            <motion.div
              className="flex items-center space-x-2 px-4 py-2 text-apple-gray-500 hover:text-apple-gray-900 hover:bg-apple-gray-100 rounded-apple transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">
                {i18n.language === 'en' ? t('chinese') : t('english')}
              </span>
            </motion.div>
          </Link>
        </div>
        
        {/* Divider */}
        <div className="h-px bg-apple-gray-200 max-w-xs mx-auto mb-6" />

        {/* DR Badge */}
        <div className="flex justify-center mb-6">
          <motion.a
            href="https://domainrank.app/dr/speedtestry.com"
            target="_blank"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <img
              src="https://domainrank.app/api/badge/speedtestry.com?theme=light"
              alt="speedtestry.com Domain Rating"
              width="360"
              height="80"
              loading="lazy"
              decoding="async"
              className="rounded-apple"
            />
          </motion.a>
        </div>

        {/* Copyright */}
        <div className="text-center">
          <p className="text-xs text-apple-gray-400">
            © {new Date().getFullYear()} SpeedTestry. All rights reserved.
          </p>
        </div>
      </div>
    </motion.footer>
  )
}

export default Footer
